import React, { useMemo, useState } from 'react';
import { BaseLayout } from '@/shared/components/layout';
import { useProjectTeam } from '../hooks/useProjectTeam';
import type { TeamMemberRow } from '../types/types';
import { Button } from '@/shared/ui/button';
import { Badge } from '@/shared/ui/badge';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Checkbox } from '@/shared/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/ui/table';
import { Alert, AlertDescription } from '@/shared/ui/alert';
import { AlertCircle, CheckCircle2, Search, Trash2, UserPlus, Loader2 } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/custom/tabs';
import { SearchableSelect } from '@/shared/ui/custom/searchable-select';
import {
  ProjectRbacPanel,
  RoleDetailsDialog,
  useProjectPermissions,
  useProjectRoles,
} from '@/features/roles-and-permissions';
import type { ProjectRole } from '@/features/roles-and-permissions';
import { RoleCombobox } from './RoleCombobox';
import { TradePartnersTab } from './TradePartnersTab';

interface TeamMemberRoleBadgeProps {
  row: TeamMemberRow;
  rolesById: Map<number, ProjectRole>;
  rolesByName: Map<string, ProjectRole>;
  onRoleClick: (role: ProjectRole) => void;
}

const TeamMemberRoleBadge: React.FC<TeamMemberRoleBadgeProps> = ({
  row,
  rolesById,
  rolesByName,
  onRoleClick,
}) => {
  const projectRole = row.roleId
    ? rolesById.get(row.roleId)
    : rolesByName.get(row.role);

  if (!projectRole) {
    return <span className="text-sm text-foreground">{row.role}</span>;
  }

  const openDetails = () => onRoleClick(projectRole);

  return (
    <Badge
      variant="secondary"
      className="cursor-pointer font-normal hover:bg-secondary/70"
      role="button"
      tabIndex={0}
      onClick={openDetails}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          openDetails();
        }
      }}
    >
      {row.role}
    </Badge>
  );
};

export const ProjectTeam: React.FC = () => {
  const {
    data,
    isLoading,
    error,
    success,
    isAddUserModalOpen,
    setIsAddUserModalOpen,
    isDeleteModalOpen,
    setIsDeleteModalOpen,
    formType,
    setFormType,
    addInternalUserForm,
    setAddInternalUserForm,
    inviteInternalUserForm,
    setInviteInternalUserForm,
    inviteExternalUserForm,
    setInviteExternalUserForm,
    handleAddInternalUser,
    handleInviteInternalUser,
    handleInviteExternalUser,
    handleDeleteUsers,
    selectedUsers,
    toggleUserSelection,
    toggleAllUsers,
    teamMemberRows,
    uniqueRoles,
    searchQuery,
    setSearchQuery,
    roleFilter,
    setRoleFilter,
    availableUsers,
    loadingUsers,
    setError,
    setSuccess,
    isMutating,
  } = useProjectTeam();

  const projectId = data?.project.id;
  const capabilities = data?.capabilities;
  const canManageMemberships = Boolean(
    capabilities?.can_manage_memberships ??
      capabilities?.can_manage_project_memberships,
  );
  const canManageTradePartners = Boolean(
    capabilities?.can_manage_trade_partners ?? canManageMemberships,
  );
  const canManageRbac = Boolean(capabilities?.can_manage_rbac);
  const requestedTab = new URLSearchParams(window.location.search).get('tab');
  const initialTab = requestedTab === 'rbac' && canManageRbac ? 'rbac' : 'members';

  const [viewingRole, setViewingRole] = useState<ProjectRole | null>(null);
  const [roleDetailsOpen, setRoleDetailsOpen] = useState(false);

  const { data: projectRoles = [] } = useProjectRoles(projectId);
  const { data: projectPermissions = [] } = useProjectPermissions(projectId);

  const rolesById = useMemo(
    () => new Map(projectRoles.map((role) => [role.id, role])),
    [projectRoles],
  );
  const rolesByName = useMemo(
    () => new Map(projectRoles.map((role) => [role.name, role])),
    [projectRoles],
  );

  const openRoleDetails = (role: ProjectRole) => {
    setViewingRole(role);
    setRoleDetailsOpen(true);
  };

  if (isLoading && !data) {
    return (
      <BaseLayout>
        <div className="flex items-center justify-center min-h-screen">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </BaseLayout>
    );
  }

  const membersTab = (
    <div className="bg-card rounded-lg shadow border border-border">
      {/* Table Header */}
      <div className="p-4 border-b border-border flex items-center justify-between">
              <div className="flex items-center space-x-4">
                {/* Filter Button */}
                <Select value={roleFilter} onValueChange={setRoleFilter}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Filter by role" />
                  </SelectTrigger>
                  <SelectContent>
                    {uniqueRoles.map(role => (
                      <SelectItem key={role} value={role}>
                        {role}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Search Bar */}
                <div className="relative w-48">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="h-10 pl-9"
                  />
                </div>
              </div>

              {canManageMemberships && (
                <div className="flex items-center space-x-2">
                  {selectedUsers.size > 0 && (
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => setIsDeleteModalOpen(true)}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete ({selectedUsers.size})
                    </Button>
                  )}
                  <Button
                    size="sm"
                    onClick={() => setIsAddUserModalOpen(true)}
                  >
                    <UserPlus className="h-4 w-4 mr-2" />
                    Add Member
                  </Button>
                </div>
              )}
            </div>

            {/* Table */}
            <Table>
              <TableHeader>
                <TableRow>
                  {canManageMemberships && (
                    <TableHead className="w-12">
                      <Checkbox
                        aria-label="Select all members"
                        checked={selectedUsers.size === teamMemberRows.length && teamMemberRows.length > 0}
                        onCheckedChange={toggleAllUsers}
                      />
                    </TableHead>
                  )}
                  <TableHead>Role</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Phone #</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {teamMemberRows.length > 0 ? (
                  teamMemberRows.map(row => {
                    const key = `${row.type}-${row.id}`;
                    return (
                      <TableRow key={key}>
                        {canManageMemberships && (
                          <TableCell>
                            <Checkbox
                              aria-label={`Select ${row.name}`}
                              checked={selectedUsers.has(key)}
                              onCheckedChange={() => toggleUserSelection(key)}
                            />
                          </TableCell>
                        )}
                        <TableCell>
                          <TeamMemberRoleBadge
                            row={row}
                            rolesById={rolesById}
                            rolesByName={rolesByName}
                            onRoleClick={openRoleDetails}
                          />
                        </TableCell>
                        <TableCell>
                          {row.name}
                          {row.isPending && (
                            <span className="ml-2 text-xs text-muted-foreground">(Pending)</span>
                          )}
                        </TableCell>
                        <TableCell>{row.email}</TableCell>
                        <TableCell>{row.phone}</TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={canManageMemberships ? 5 : 4}
                      className="text-center text-muted-foreground"
                    >
                      No members found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
    </div>
  );

  return (
    <BaseLayout>
      <div className="min-h-screen bg-background p-6">
        <div className="max-w-7xl mx-auto">
          {/* Messages */}
          {error && (
            <Alert variant="destructive" className="mb-6">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {success && (
            <Alert className="mb-6 border-primary/30 bg-primary/10 text-foreground">
              <CheckCircle2 className="h-4 w-4 text-primary" />
              <AlertDescription>{success}</AlertDescription>
            </Alert>
          )}

          <Tabs defaultValue={initialTab} className="w-full">
            <TabsList className="mb-6">
              <TabsTrigger value="members">Members</TabsTrigger>
              <TabsTrigger value="trade-partners">Trade Partners</TabsTrigger>
              {canManageRbac && (
                <TabsTrigger value="rbac" className="gap-1.5">
                  Roles &amp; Permissions
                </TabsTrigger>
              )}
            </TabsList>
            <TabsContent value="members" className="mt-0">
              {membersTab}
            </TabsContent>
            {projectId && data?.client.id && (
              <TabsContent value="trade-partners" className="mt-0">
                <TradePartnersTab
                  projectId={projectId}
                  clientId={data.client.id}
                  canManage={canManageTradePartners}
                  initialData={{
                    trade_partners: data.trade_partners ?? [],
                    trades: data.trades ?? [],
                    project_roles: data.project_roles ?? [],
                    available_clients: data.available_clients ?? [],
                  }}
                  onError={setError}
                  onSuccess={setSuccess}
                />
              </TabsContent>
            )}
            {canManageRbac && (
              <TabsContent value="rbac" className="mt-0">
                <ProjectRbacPanel projectId={projectId} />
              </TabsContent>
            )}
          </Tabs>

          {/* Add User Modal */}
          {canManageMemberships && (
          <Dialog open={isAddUserModalOpen} onOpenChange={setIsAddUserModalOpen}>
            <DialogContent className="sm:max-w-[600px]">
              <DialogHeader>
                <DialogTitle>Add Member to {data?.project.name}</DialogTitle>
                <DialogDescription>
                  Add an existing user or invite a new member to the project
                </DialogDescription>
              </DialogHeader>

              {/* Tab Toggle */}
              <Tabs value={formType} onValueChange={(v) => setFormType(v as 'internal' | 'external')}>
                <TabsList className="grid w-full grid-cols-2 mb-2">
                  <TabsTrigger value="internal">Internal</TabsTrigger>
                  <TabsTrigger value="external">External</TabsTrigger>
                </TabsList>

                {/* Internal Forms Tab */}
                <TabsContent value="internal" className="space-y-6">
                  {/* Add Existing User Form */}
                  <div className="space-y-4">
                    <h3 className="font-semibold">Add Existing User</h3>
                    <form onSubmit={handleAddInternalUser} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="user">User *</Label>
                        {loadingUsers ? (
                          <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            <span>Loading users...</span>
                          </div>
                        ) : (
                          <SearchableSelect
                            value={addInternalUserForm.user_id}
                            onValueChange={(value) =>
                              setAddInternalUserForm(prev => ({ ...prev, user_id: value }))
                            }
                            options={availableUsers.map(user => ({
                              value: String(user.id),
                              label: `${user.first_name} ${user.last_name} (${user.email})`
                            }))}
                            placeholder="Select a user"
                            searchPlaceholder="Search users..."
                            emptyMessage="No users found."
                          />
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="add-role">Role *</Label>
                        <RoleCombobox
                          projectId={projectId}
                          value={addInternalUserForm.role_id}
                          onChange={(roleId) =>
                            setAddInternalUserForm(prev => ({ ...prev, role_id: roleId }))
                          }
                          canManageRbac={canManageRbac}
                        />
                      </div>

                      <Button type="submit" className="w-full" disabled={isMutating || !addInternalUserForm.role_id}>
                        Add Member
                      </Button>
                    </form>
                  </div>

                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t border-border" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-popover px-2 text-muted-foreground">or</span>
                    </div>
                  </div>

                  {/* Invite New User Form */}
                  <div className="space-y-4">
                    <h3 className="font-semibold">Invite New User</h3>
                    <form onSubmit={handleInviteInternalUser} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="invite-email">Email Address *</Label>
                        <Input
                          id="invite-email"
                          type="email"
                          value={inviteInternalUserForm.email}
                          onChange={(e) =>
                            setInviteInternalUserForm(prev => ({ ...prev, email: e.target.value }))
                          }
                          placeholder="Enter email"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="invite-role">Role *</Label>
                        <RoleCombobox
                          projectId={projectId}
                          value={inviteInternalUserForm.role_id}
                          onChange={(roleId) =>
                            setInviteInternalUserForm(prev => ({ ...prev, role_id: roleId }))
                          }
                          canManageRbac={canManageRbac}
                        />
                      </div>

                      <Button
                        type="submit"
                        className="w-full"
                        disabled={isMutating || !inviteInternalUserForm.role_id}
                      >
                        Send Invitation
                      </Button>
                    </form>
                  </div>
                </TabsContent>

                {/* External Form Tab */}
                <TabsContent value="external">
                  <form onSubmit={handleInviteExternalUser} className="space-y-4">
                    <p className="text-sm text-muted-foreground">* = required field</p>

                    <div className="space-y-2">
                      <Label htmlFor="ext-role">Role *</Label>
                      <RoleCombobox
                        projectId={projectId}
                        value={inviteExternalUserForm.role_id}
                        onChange={(roleId) =>
                          setInviteExternalUserForm(prev => ({ ...prev, role_id: roleId }))
                        }
                        canManageRbac={canManageRbac}
                      />
                      <p className="text-xs text-muted-foreground">
                        The selected role's permissions determine what this user can access.
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="ext-email">Email Address *</Label>
                      <Input
                        id="ext-email"
                        type="email"
                        value={inviteExternalUserForm.external_email}
                        onChange={(e) =>
                          setInviteExternalUserForm(prev => ({ ...prev, external_email: e.target.value }))
                        }
                        placeholder="Enter email"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="ext-company">Company Name *</Label>
                      <Input
                        id="ext-company"
                        value={inviteExternalUserForm.external_company_name}
                        onChange={(e) =>
                          setInviteExternalUserForm(prev => ({
                            ...prev,
                            external_company_name: e.target.value,
                          }))
                        }
                        placeholder="Enter company name"
                      />
                    </div>

                    <Button type="submit" className="w-full" disabled={isMutating || !inviteExternalUserForm.role_id}>
                      Add Member
                    </Button>
                  </form>
                </TabsContent>
              </Tabs>
            </DialogContent>
          </Dialog>
          )}

          <RoleDetailsDialog
            open={roleDetailsOpen}
            onOpenChange={(open) => {
              setRoleDetailsOpen(open);
              if (!open) setViewingRole(null);
            }}
            role={viewingRole}
            permissions={projectPermissions}
          />

          {/* Delete Confirmation Modal */}
          {canManageMemberships && (
          <Dialog open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Delete Selected Users</DialogTitle>
                <DialogDescription>
                  Are you sure you want to delete {selectedUsers.size} selected user(s)?
                  This action cannot be undone.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter className="space-x-2">
                <Button variant="outline" onClick={() => setIsDeleteModalOpen(false)}>
                  Cancel
                </Button>
                <Button variant="destructive" onClick={handleDeleteUsers} disabled={isMutating}>
                  Delete
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          )}
        </div>
      </div>
    </BaseLayout>
  );
};
